/**
 * RecentTransactionsCard
 *
 * Dashboard widget showing the 5 most-recent transactions.  Improvements
 * introduced in [T38P0]:
 *
 * • Fixed asymmetric layout — CSS grid guarantees the amount column is always
 *   right-aligned and never pushed by long descriptions/badges.
 * • Type icons (income ↑ / expense ↓ / transfer ⇄) replacing letter initials.
 * • Account name shown below amount for quick context.
 * • Per-row hover actions: View Details, Edit, Delete.
 * • Inline TransactionDetailSheet — opens on row click or "View Details".
 * • Empty state with an "Add Transaction" CTA.
 * • Skeleton loading shimmer while data is in-flight.
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
// [T149-PR1] Adopt direct Card primitives (shell retired in T158).
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowRight,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeftRight,
  Pencil,
  Trash2,
  Eye,
  MoreVertical,
  Receipt,
  Plus,
  Layers,
} from "lucide-react";
import { Link } from "react-router-dom";
import type {
  Transaction,
  Account,
  Category,
  TransactionCategory,
} from "@/lib/types";
import TransactionDetailSheet from "@/components/transactions/TransactionDetailSheet";
import { useCurrency } from "@/hooks/useCurrency";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RecentTransactionsCardProps {
  transactions: Transaction[];
  accounts?: Account[];
  categories?: Category[];
  /** Pass true while the parent is still fetching the first batch */
  isLoading?: boolean;
  /** [rev16-dashboard] When supplied, row clicks defer to the parent's
   * modal stack (the parent renders the shared `TransactionDetailSheet`).
   * When omitted, the card keeps its legacy self-contained
   * `TransactionDetailSheet` so existing callers (Dashboard.tsx,
   * LegacyDashboard.tsx) keep working without duplicate sheets. */
  onViewDetail?: (transaction: Transaction) => void;
  /** Called when the user requests to edit a transaction */
  onEdit?: (transaction: Transaction) => void;
  /** Called when the user requests to delete a transaction (passes transaction id) */
  onDelete?: (id: string) => void;
  /** How many items to show (default: 5) */
  limit?: number;
}

// ---------------------------------------------------------------------------
// Per-type visual config
// ---------------------------------------------------------------------------

const typeConfig = {
  income: {
    Icon: ArrowUpCircle,
    iconColor: "text-green-600 dark:text-green-400",
    iconBg: "bg-green-50 dark:bg-green-900/20",
    amountClass: "text-green-600 dark:text-green-400",
    prefix: "+",
  },
  expense: {
    Icon: ArrowDownCircle,
    iconColor: "text-red-500 dark:text-red-400",
    iconBg: "bg-red-50 dark:bg-red-900/20",
    amountClass: "text-foreground",
    prefix: "-",
  },
  transfer: {
    Icon: ArrowLeftRight,
    iconColor: "text-blue-500 dark:text-blue-400",
    iconBg: "bg-blue-50 dark:bg-blue-900/20",
    amountClass: "text-blue-600 dark:text-blue-400",
    prefix: "",
  },
} as const;

const categoryColorMap: Record<string, string> = {
  groceries:
    "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  shopping: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  dining:
    "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
  transportation:
    "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  entertainment:
    "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300",
  utilities:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  salary:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300",
  freelance: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300",
};

function getCategoryColor(category: string) {
  return (
    categoryColorMap[category.toLowerCase()] ||
    "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
  );
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Skeleton row
// ---------------------------------------------------------------------------

function SkeletonRow() {
  return (
    <div className="grid grid-cols-[40px_1fr_auto] gap-3 items-center py-3 px-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="space-y-1.5">
        <Skeleton className="h-3.5 w-36" />
        <Skeleton className="h-3 w-24" />
      </div>
      <div className="text-right space-y-1.5">
        <Skeleton className="h-3.5 w-16 ml-auto" />
        <Skeleton className="h-3 w-20 ml-auto" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single row component
// ---------------------------------------------------------------------------

interface TransactionRowProps {
  transaction: Transaction;
  accountName: string | null;
  onViewDetail: (t: Transaction) => void;
  onEdit?: (t: Transaction) => void;
  onDelete?: (id: string) => void;
}

function TransactionRow({
  transaction,
  accountName,
  onViewDetail,
  onEdit,
  onDelete,
}: TransactionRowProps) {
  const cfg = typeConfig[transaction.type] ?? typeConfig.expense;
  const { Icon, iconColor, iconBg, amountClass, prefix } = cfg;
  const { fmt } = useCurrency();

  return (
    <TooltipProvider delayDuration={400}>
      {/*
       * Three-column grid:
       *   col 1 [40px] — fixed avatar/icon
       *   col 2 [1fr]  — description + meta — expands to fill space
       *   col 3 [auto] — amount block — always right-aligned, shrinks to fit
       * This eliminates the flex justify-between asymmetry where long text
       * would push the amount off to the right with no constraint.
       */}
      <div
        data-testid="transaction-row"
        className="group grid grid-cols-[40px_1fr_auto] gap-3 items-center
          rounded-lg px-3 py-2.5 -mx-3
          cursor-pointer select-none
          transition-colors duration-150
          hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2
          focus-visible:ring-ring"
        role="button"
        tabIndex={0}
        onClick={() => onViewDetail(transaction)}
        onKeyDown={(e) => e.key === "Enter" && onViewDetail(transaction)}
        aria-label={`View details for ${transaction.description}`}
      >
        {/* Col 1 — avatar icon (prefer emoji when available) */}
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center
            rounded-full border border-border/40 ${iconBg}`}
        >
          {transaction.emoji ? (
            <span className="text-lg leading-none">{transaction.emoji}</span>
          ) : (
            <Icon className={`h-4 w-4 ${iconColor}`} />
          )}
        </div>

        {/* Col 2 — description, date, category badge */}
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight truncate">
            {transaction.description}
            {/* T80P1: Items count badge */}
            {transaction.items && transaction.items.length > 0 && (
              <Badge
                variant="secondary"
                className="ml-1.5 text-[10px] px-1.5 py-0 h-4 leading-none align-middle inline-flex"
              >
                <Layers className="h-3 w-3 mr-0.5" />
                {transaction.items.length}
              </Badge>
            )}
          </p>
          <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatDate(transaction.date)}
            </span>
            {transaction.category && (
              <Badge
                variant="secondary"
                className={`${getCategoryColor(
                  typeof transaction.category === "string"
                    ? transaction.category
                    : ((transaction.category as TransactionCategory | undefined)
                        ?.name ?? ""),
                )} border-0 text-[10px] px-1.5 py-0 h-4 leading-none`}
              >
                {typeof transaction.category === "string"
                  ? transaction.category
                  : (transaction.category as TransactionCategory | undefined)
                      ?.name}
              </Badge>
            )}
            {transaction.isPending && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 h-4 leading-none text-amber-600 border-amber-300"
              >
                Pending
              </Badge>
            )}
          </div>
        </div>

        {/* Col 3 — amount + account name + hover actions */}
        <div className="flex items-center gap-1.5 justify-end shrink-0">
          {/* Mobile: compact dropdown menu to save space [T45P0] */}
          <div
            className="flex md:hidden items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Transaction actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={() => onViewDetail(transaction)}>
                  <Eye className="h-3.5 w-3.5 mr-2" />
                  View Details
                </DropdownMenuItem>
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(transaction)}>
                    <Pencil className="h-3.5 w-3.5 mr-2" />
                    Edit
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(transaction.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop: hover-to-reveal action buttons */}
          <div
            className="hidden md:group-hover:flex items-center gap-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            {onEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onEdit(transaction)}
                    aria-label="Edit transaction"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Edit</TooltipContent>
              </Tooltip>
            )}

            {onDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => onDelete(transaction.id)}
                    aria-label="Delete transaction"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Delete</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Amount block */}
          <div className="text-right tabular-nums min-w-0">
            <p className={`text-sm font-semibold ${amountClass} truncate`}>
              {prefix}
              {fmt(Math.abs(transaction.amount))}
            </p>
            {accountName && (
              <p className="text-[10px] text-muted-foreground truncate max-w-[100px] md:max-w-[110px]">
                {accountName}
              </p>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Main exported card
// ---------------------------------------------------------------------------

export default function RecentTransactionsCard({
  transactions,
  accounts = [],
  categories = [],
  isLoading = false,
  onViewDetail,
  onEdit,
  onDelete,
  limit = 5,
}: RecentTransactionsCardProps) {
  // [rev16-dashboard] Self-contained local sheet state. Used only when no
  // parent `onViewDetail` handler is supplied (Dashboard.tsx,
  // LegacyDashboard.tsx). When the parent owns the modal stack, this state
  // is initialized but never read — the local TransactionDetailSheet below
  // short-circuits to avoid a duplicate modal.
  const [detailTransaction, setDetailTransaction] =
    useState<Transaction | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // [T28P0] Spread before sort; Redux freezes state objects in development.
  const recentTransactions = [...transactions]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, limit);

  // [rev16-dashboard] When the parent owns the modal stack, defer the row
  // detail click to the parent's handler instead of opening the legacy local
  // sheet. Otherwise fall back to the legacy local state path so Dashboard.tsx
  // and LegacyDashboard.tsx callers keep working unchanged.
  const handleViewDetail = (transaction: Transaction) => {
    if (onViewDetail) {
      onViewDetail(transaction);
      return;
    }
    setDetailTransaction(transaction);
    setDetailOpen(true);
  };

  // Build a lookup map once instead of scanning on every row render
  const accountMap = Object.fromEntries(accounts.map((a) => [a.id, a.name]));

  return (
    <>
      <Card
        data-testid="home-card-list-recent"
        className="glass-panel h-full transition-all duration-200 hover:shadow-md hover:border-primary/20"
      >
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <Receipt className="h-5 w-5 text-primary" />
              </div>
              <CardTitle>Recent Transactions</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-primary"
              asChild
            >
              <Link to="/transactions" className="flex items-center gap-1">
                View All <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </>
        </CardHeader>
        <CardContent>
          {/* Loading skeleton */}
          {isLoading && recentTransactions.length === 0 ? (
            <div className="divide-y divide-border/40">
              {Array.from({ length: limit }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : recentTransactions.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/60 mb-3">
                <Receipt className="h-7 w-7 text-muted-foreground" />
              </div>
              <h4 className="text-sm font-semibold text-foreground mb-1">
                No transactions yet
              </h4>
              <p className="text-xs text-muted-foreground mb-4 max-w-[200px]">
                Add your first transaction to start tracking your finances.
              </p>
              <Button size="sm" asChild>
                <Link to="/transactions" className="flex items-center gap-1.5">
                  <Plus className="h-3.5 w-3.5" />
                  Add Transaction
                </Link>
              </Button>
            </div>
          ) : (
            /* Transaction list */
            <div className="divide-y divide-border/40 -my-1">
              {recentTransactions.map((transaction) => (
                <TransactionRow
                  key={transaction.id}
                  transaction={transaction}
                  accountName={accountMap[transaction.accountId] ?? null}
                  onViewDetail={handleViewDetail}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* [rev16-dashboard] Detail side-panel — only rendered when no parent
           `onViewDetail` handler is supplied. When the parent owns the stack,
           the parent renders the shared TransactionDetailSheet itself and we
           suppress the local sheet to avoid duplicate modals (Dashboard.tsx
           and LegacyDashboard.tsx callers continue to use the self-contained
           path unchanged). */}
      {!onViewDetail && (
        <TransactionDetailSheet
          transaction={detailTransaction}
          open={detailOpen}
          onOpenChange={setDetailOpen}
          accounts={accounts}
          categories={categories}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </>
  );
}
